<div class="flex flex-col gap-3">
  <!-- The drag and drop / generic upload box -->
    <!-- EXISTING REMOTE IMAGE STATE -->
    @if (existingImageUrl && processedFiles.length === 0 && !multiple) {
      <div class="relative w-full min-w-0 rounded-xl border-2 border-base-200 shadow-sm bg-base-100 p-2 flex flex-col gap-3">
        <div class="flex min-w-0 items-center gap-3 w-full">
          <div class="w-16 h-16 rounded-lg bg-base-200 overflow-hidden flex-shrink-0">
            <img [src]="existingImageUrl" alt="Existing upload" class="w-full h-full object-contain" />
          </div>
          <div class="min-w-0 flex-1 overflow-hidden">
            <span class="text-xs font-bold uppercase tracking-widest text-primary block leading-none mb-1">Existing active image</span>
            <span class="text-sm font-semibold truncate block text-base-content">{{ existingImageUrl.split('/').pop() || 'current_image.png' }}</span>
          </div>
        </div>
        <div class="flex flex-wrap gap-2">
          <button type="button" class="btn btn-sm btn-outline shadow-sm" (click)="fileInput.click()">
            <i class="fi fi-rs-picture"></i> Change
          </button>
          <button type="button" class="btn btn-sm btn-error btn-outline shadow-sm" aria-label="Remove image" (click)="removeExistingImage()">
            <i class="fi fi-rs-trash"></i>
          </button>
        </div>
      </div>
    }

    <input
      #fileInput
      type="file"
      class="hidden"
      [multiple]="multiple"
      [accept]="config.allowedFormats.join(',')"
      (change)="onFileSelected($event)"
    />

    <!-- DEFAULT UPLOADER -->
    @if (!existingImageUrl || processedFiles.length > 0 || multiple) {
      <div
        class="relative flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-xl cursor-pointer bg-slate-50 hover:bg-slate-100 transition-colors"
        [ngClass]="{
          'border-slate-300': !isDragging,
          'border-primary-500 bg-primary-50': isDragging
        }"
        (dragover)="onDragOver($event)"
        (dragleave)="onDragLeave($event)"
        (drop)="onDrop($event)"
        (click)="fileInput.click()"
      >
        <div class="flex flex-col items-center justify-center pt-5 pb-6">
          <i class="fi fi-rs-cloud-upload text-3xl text-slate-400 mb-2"></i>
          <p class="mb-1 text-sm text-slate-500 font-semibold">
            <span class="text-slate-700">Click to upload</span> or drag and drop
          </p>
          <p class="text-xs font-black text-slate-700">{{ config.label }}</p>
          <p class="text-[11px] text-slate-400 text-center px-4 max-w-sm">
            {{ config.note }}
          </p>
        </div>
      </div>
    }

  <!-- Requirements / Configuration Display -->
  <div class="rounded-xl border border-slate-100 bg-white p-3 flex items-start gap-3 shadow-sm">
    <i class="fi fi-rs-info text-slate-400 mt-0.5 text-sm"></i>
    <div class="min-w-0 flex-1">
      <p class="text-xs font-bold text-slate-700 mb-1">
        {{ config.label }} · Recommended: {{ config.recommendedWidth }} × {{ config.recommendedHeight }} px
      </p>
      <div class="flex flex-wrap gap-x-4 gap-y-1 text-[11px] font-medium text-slate-500">
        <span>Ratio: {{ ratioLabel }} (±{{ config.ratioTolerance * 100 | number:'1.0-0' }}%)</span>
        <span>Min: {{ config.minWidth }} × {{ config.minHeight }} px</span>
        <span>Max: {{ config.maxFilesizeKb }} KB</span>
        <span>Formats: {{ acceptedFormatLabel }}</span>
      </div>
      <p class="mt-2 text-[10px] font-semibold text-slate-500">{{ config.backgroundGuidance }}</p>
      <p class="mt-1 text-[10px] font-semibold text-slate-500">{{ config.safeArea }}</p>
      <p class="mt-1 text-[10px] font-semibold text-slate-600">{{ config.compositionGuidance }}</p>
    </div>
  </div>

  @if (previewUrl) {
    <div class="rounded-xl border border-slate-200 bg-slate-50 p-3">
      <p class="mb-2 text-[9px] font-black uppercase tracking-widest text-slate-400">Customer App Preview</p>
      @switch (config.previewKind) {
        @case ('home-tab') {
          <div class="flex flex-col items-center gap-1.5">
            <div class="w-[42px] h-[42px] rounded-full bg-[#fff4e5] flex items-center justify-center overflow-hidden">
              <img [src]="previewUrl" class="w-[30px] h-[30px] object-contain" alt="Home tab icon preview" />
            </div>
            <span class="text-[10px] font-bold text-slate-700">Tab label</span>
          </div>
        }
        @case ('bottom-tab') {
          <div class="mx-auto w-[120px] h-[54px] rounded-xl flex items-center justify-center overflow-hidden shadow-[0_4px_8px_rgba(0,0,0,0.14)]">
            <img [src]="previewUrl" class="w-full h-full object-cover" alt="Bottom tab artwork preview" />
          </div>
        }
        @case ('featured-card') {
          <div class="relative mx-auto w-full max-w-[300px] aspect-[30/17] overflow-hidden rounded-2xl bg-white">
            <img [src]="previewUrl" class="absolute inset-0 size-full object-cover" alt="Featured card preview" />
            <div class="absolute inset-y-0 left-0 w-[60%] border-r border-dashed border-white/80 bg-gradient-to-r from-white/65 to-transparent p-3">
              <span class="text-[10px] font-black text-slate-800">App copy safe area</span>
            </div>
          </div>
        }
        @case ('section-header') {
          <div class="mx-auto w-full overflow-hidden rounded-lg bg-slate-200" [style.aspectRatio]="config.targetRatio">
            <img [src]="previewUrl" class="size-full object-cover" alt="Banner preview" />
          </div>
        }
        @case ('tracking-ad') {
          <div class="mx-auto w-full max-w-[405px] overflow-hidden rounded-xl border border-slate-200 bg-white" [style.aspectRatio]="2.7">
            <img [src]="previewUrl" class="size-full object-cover" alt="Live tracking advertisement preview" />
          </div>
        }
        @default {
          <div class="mx-auto w-full max-w-72 overflow-hidden rounded-lg bg-white" [style.aspectRatio]="config.targetRatio">
            <img [src]="previewUrl" class="size-full" [ngClass]="config.fitMode === 'contain' ? 'object-contain' : 'object-cover'" alt="Image preview" />
          </div>
        }
      }
    </div>
  }

  <!-- Processing / Feedback Results for files -->
  @if (processedFiles.length > 0) {
    <div class="flex flex-col gap-2 mt-2">
      @for (pf of processedFiles; track pf.file.name) {
        <div 
          class="rounded-xl border p-3 flex flex-col gap-2"
          [ngClass]="{
            'border-red-200 bg-red-50': pf.status === 'NEW_REJECTED_FATAL' || pf.status === 'NEW_REJECTED_FIXABLE',
            'border-emerald-100 bg-emerald-50': pf.status === 'NEW_ACCEPTED' && !hasWarnings(pf),
            'border-amber-200 bg-amber-50': pf.status === 'NEW_ACCEPTED' && hasWarnings(pf),
            'border-slate-200 bg-slate-50': pf.status === 'NEW_PENDING'
          }"
        >
          <div class="flex items-center justify-between">
            <div class="flex items-center gap-2 overflow-hidden">
              <i 
                class="text-sm mt-0.5"
                [ngClass]="{
                  'fi-rs-cross-circle text-red-600': pf.status === 'NEW_REJECTED_FATAL' || pf.status === 'NEW_REJECTED_FIXABLE',
                  'fi-rs-check-circle text-emerald-600': pf.status === 'NEW_ACCEPTED' && !hasWarnings(pf),
                  'fi-rs-engine-warning text-amber-600': pf.status === 'NEW_ACCEPTED' && hasWarnings(pf),
                  'fi-rs-spinner-indicator animate-spin text-slate-400': pf.status === 'NEW_PENDING'
                }"
              ></i>
              <span class="text-xs font-bold text-slate-800 truncate">{{ pf.file.name }}</span>
            </div>
            
            <button 
              type="button" 
              class="text-slate-400 hover:text-slate-700 p-1 rounded-md"
              (click)="removeProcessedFile(pf)"
            >
              <i class="fi fi-rs-cross-small text-xs"></i>
            </button>
          </div>

          <!-- Rejected State -->
          @if (pf.status === 'NEW_REJECTED_FATAL' || pf.status === 'NEW_REJECTED_FIXABLE') {
            <div class="pl-6 gap-1 flex flex-col">
              <span class="text-xs font-black text-red-700">Image rejected</span>
              @if (pf.width && pf.height) {
                <span class="text-[11px] text-red-600 font-medium">Uploaded: {{ pf.width }} × {{ pf.height }} px</span>
              }
              <ul class="list-disc pl-4 mt-1">
                @for (issue of getBlockingIssues(pf); track issue.message) {
                  <li class="text-[11px] text-red-600 font-semibold">{{ issue.message }}</li>
                }
              </ul>
              
              <div class="flex items-center gap-3 mt-2">
                @if (pf.status === 'NEW_REJECTED_FIXABLE') {
                  <button type="button" class="text-xs font-bold px-3 py-1.5 bg-slate-800 text-white rounded-md hover:bg-slate-700 transition" (click)="openEditor(pf)">
                    <i class="fi fi-rs-magic-wand mr-1 text-[10px]"></i> Fix image
                  </button>
                }
                <span class="text-[11px] text-red-500">Please choose another image.</span>
              </div>
            </div>
          }

          <!-- Accepted with Warnings State -->
          @if (pf.status === 'NEW_ACCEPTED' && hasWarnings(pf)) {
            <div class="pl-6 gap-1 flex flex-col">
              <span class="text-xs font-black text-amber-700">Ready to upload, but see notes:</span>
              <span class="text-[11px] text-amber-700 font-medium">Uploaded: {{ pf.width }} × {{ pf.height }} px</span>
              <ul class="list-disc pl-4 mt-1">
                @for (issue of getWarningIssues(pf); track issue.message) {
                  <li class="text-[11px] text-amber-700 font-semibold">{{ issue.message }}</li>
                }
              </ul>
              <div class="flex items-center gap-3 mt-2">
                <button type="button" class="text-[10px] font-semibold px-2 py-1 bg-slate-100 text-slate-700 border border-slate-300 rounded-md hover:bg-slate-200 transition" (click)="openEditor(pf, 'Review or edit image properties')">
                  <i class="fi fi-rs-pencil mr-1 text-[9px]"></i> Edit image
                </button>
              </div>
            </div>
          }

          <!-- Accepted completely fine State -->
          @if (pf.status === 'NEW_ACCEPTED' && !hasWarnings(pf)) {
            <div class="pl-6 flex items-center justify-between">
              <span class="text-[11px] text-emerald-700 font-bold">✓ Ready for upload ({{ pf.width }} × {{ pf.height }} px)</span>
              
              <button type="button" class="text-[10px] font-semibold px-2 py-1 bg-emerald-100/50 text-emerald-800 border border-emerald-200 rounded-md hover:bg-emerald-100 transition shadow-sm" (click)="openEditor(pf, 'Adjust or manually crop image')">
                <i class="fi fi-rs-pencil mr-1 text-[9px]"></i> Edit image
              </button>
            </div>
          }
        </div>
      }
    </div>
  }
</div>

@if (activeFileToEdit && activeFileMetadata) {
  <app-image-editor-modal
    [originalFile]="activeFileToEdit.file"
    [config]="config"
    [metadata]="activeFileMetadata"
    [fixReason]="activeFileFixReason"
    (saved)="onEditorSaved($event)"
    (cancelled)="closeEditor()"
  ></app-image-editor-modal>
}
